<template>
    <div class="pagination-container font-4">
        <div class="total-rows">
            Showing rows&nbsp;
            <span class="font-4">{{fromRow}}-{{toRow}}</span>
            out of <span class="font-4">{{total}}</span> entries
        </div>
        <div class="page-controls">
            <div>
                <span class="font-4">Rows per page:</span>
                <Dropdown unique-id="pageSizeDropdown"
                          :options="pageSizeOptions"
                          optionsTitle="Size"
                          class="page-size-dropdown font-7"
                          :selected-id="pageSize"
                          height="27"
                          markSelected
                          selected-display-text-field="titleText"
                          @dropdownSelectedItemChanged="pageSizeChanged"></Dropdown>
                <span class="font-4">Go to page:</span>
                <input type="text"
                       class="go-to-page font-5"
                       @focus="$event.target.select()"
                       @input="onGoToPageChanged"
                       v-model="goToPageValue">
                <span class="font-4 out-of-text">Out of {{totalPages}}</span>
                <span class="page-buttons">
                    <button :disabled="page === 1"
                            @click="pageChanged(page - 1)">
                        <IconBase icon-name="arrowRightIcon" width="5" height="9" class="previous-page-icon"></IconBase>
                    </button>
                    <button :disabled="page === totalPages"
                            @click="pageChanged(page + 1)">
                        <IconBase icon-name="arrowRightIcon" width="5" height="9"></IconBase>
                    </button>
                </span>
            </div>
        </div>
    </div>
</template>

<script>
import Dropdown from 'components/dropdowns/Dropdown.vue';
import IconBase from 'components/IconBase.vue';

export default {
    name: 'Pagination',
    components: { IconBase, Dropdown },
    data() {
        return {
            goToPageValue: this.page
        };
    },
    watch: {
        page(newPage) {
            this.goToPageValue = newPage;
        },
        pageSize() {
            this.resetPagination();
        },
        total() {
            this.resetPagination();
        }
    },
    props: {
        page: {
            type: Number,
            required: true
        },
        pageSize: {
            type: Number,
            required: true
        },
        total: {
            type: Number,
            required: true
        },
        pageSizes: {
            type: Array,
            default: () => [10, 20, 50, 100, 500]
        }
    },
    computed: {
        totalPages() {
            return Math.ceil(this.total / this.pageSize);
        },
        fromRow() {
            return ((this.page - 1) * this.pageSize) + 1;
        },
        toRow() {
            const toRow = this.fromRow + this.pageSize - 1;
            return Math.min(toRow, this.total);
        },
        pageSizeOptions() {
            return this.pageSizes.map((size) => ({ id: size, titleText: size, displayText: size }));
        }
    },
    methods: {
        pageSizeChanged({ id: pageSize }) {
            this.$emit('pageSizeChanged', pageSize);
        },
        pageChanged(page) {
            this.$emit('pageChanged', page);
        },
        onGoToPageChanged(event) {
            const toPage = Number(event.target.value);

            if (Number.isNaN(toPage)) {
                this.goToPageValue = this.page;
                event.target.blur();
                return;
            }

            if (toPage > this.totalPages) {
                this.pageChanged(this.totalPages);
                this.goToPageValue = this.totalPages;
                event.target.blur();
            } else if (toPage < 1) {
                this.pageChanged(1);
                this.goToPageValue = 1;
                event.target.blur();
            } else {
                this.pageChanged(toPage);
            }
        },
        resetPagination() {
            this.$emit('pageChanged', 1);
        }
    }
};
</script>

<style lang="scss" scoped>
@import "~styles/styleGuide";
@import "~styles/buttons";

.pagination-container {
    display: flex;
    margin: 20px 0;

    .font-4 {
        color: $dark-300;
    }
}

.total-rows {
    flex: 1;
    color: $light-800;
}

.page-controls {
    flex: 2;
    display: flex;
    justify-content: flex-end;
    line-height: 25px;

    .font-4 {
        margin-right: 5px;
    }

    .out-of-text {
        margin-left: 10px;
    }

    .go-to-page {
        width: 50px;
        border-radius: 3px;
        height: 25px;
        border: 1px solid $light-500;
        padding-right: 10px;
        padding-left: 10px;

        &:focus {
            outline: none;
            border: 1px solid $primary-500;
        }
    }

    .page-size-dropdown {
        width: 65px;
        margin-right: 1rem;
    }

    .page-buttons {
        margin-left: 5px;
        display: inline-flex;
    }

    button {
        height: 25px;
        min-width: 25px;
        padding: 0;

        &:first-child {
            border-right: 0;
            border-top-right-radius: 0;
            border-bottom-right-radius: 0;
        }

        &:last-child {
            border-top-left-radius: 0;
            border-bottom-left-radius: 0;
        }

        &:disabled {
            opacity: 0.5;
        }

        .previous-page-icon {
            transform: rotate(180deg);
            margin-top: 2px;
        }
    }
}
</style>
